/**
 * Product Development & Formulation — 16 documents.
 *
 * The questions this section exists to answer, spread across the entries where each one
 * actually bites: who owns the formula, whether the lab can reuse it, whether the flavor
 * house can restrict production, whether the founder can take the formula to another
 * manufacturer, who owns modifications, confidentiality, minimum purchases, ingredient
 * exclusivity, who is responsible for testing, and who is responsible for regulatory work.
 */
export const PRODUCT_DEVELOPMENT_DOCS = [
  {
    id: "food-scientist-agreement",
    name: "Food Scientist Agreement",
    category: "product-development",
    subcategory: "Development talent",
    aliases: ["Food Technologist Agreement", "Product Developer Agreement", "Formulator Engagement Agreement"],
    stages: ["idea", "development", "pre-launch"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Food Scientist (individual or their firm)",
    whenYouNeedIt:
      "When you engage an individual food scientist or technologist to build or fix a formulation — including the very common case where they are doing it evenings and weekends alongside a day job at a larger food company.",
    whatItDoes:
      "Sets the scope of the formulation work, the deliverables, the fee, and — the part that matters most — who owns the resulting formula, the bench records and any improvement made later.",
    whyItMatters:
      "Individual food scientists are the most common starting point for a first product and the most common source of a broken chain of title. Two things go wrong. The scientist works for a large manufacturer whose own employment agreement may claim what they invent, so the person selling you a formula may not be free to sell it. And the engagement is papered as a consulting invoice with no assignment, so the company owns a relationship rather than an asset. Both surface at the same moment: when an acquirer or an investor asks to see the document that makes the formula yours.",
    majorTerms: [
      "Scope of work and number of formulation rounds",
      "Deliverables: formula, specification, process parameters, bench records",
      "Fee structure — hourly, per project, or per milestone",
      "Present assignment of the formula and all work product",
      "Ownership of modifications and later improvements",
      "Confidentiality and non-use across their other clients",
      "Representation that they are free of conflicting obligations",
      "Restriction on developing a similar product for others during the engagement",
      "Attendance at pilot and scale-up runs",
      "Ongoing support and reformulation rates after handover",
    ],
    clausesToWatch: [
      {
        clause: "Freedom-to-contract representation",
        why: "A scientist bound by an employer's invention assignment may not own what they are purporting to transfer to you.",
      },
      {
        clause: "Present assignment of work product",
        why: "A promise to assign later leaves you dependent on a cooperative individual years after the engagement ended.",
      },
      {
        clause: "Bench records and trial data",
        why: "The reasoning behind the formula lives in the notes; a deliverable defined as 'the final formula' leaves the useful knowledge behind.",
      },
      {
        clause: "Non-use across other clients",
        why: "Independent formulators often work several brands in one category, and reuse of your approach is a use question, not a disclosure question.",
      },
      {
        clause: "Scale-up support",
        why: "A bench formula that has never been produced at plant scale is an unfinished deliverable, and support during scale-up is where the fee arguments start.",
      },
      {
        clause: "Post-engagement reformulation",
        why: "Without agreed rates and availability, a required ingredient substitution later becomes an emergency negotiation.",
      },
    ],
    cpgIssues: [
      "Bench formulas rarely survive scale-up unchanged; homogenisation, thermal process and fill temperature at a plant change the product, and the agreement should say who does that work and who owns the result.",
      "The scientist may specify ingredients from suppliers who will not sell at your volume, so supplier availability at your MOQ belongs in the deliverable, not in a later surprise.",
      "Costed formulas matter more than working formulas — ask for the formula delivered with a cost per unit at stated volumes and named suppliers.",
      "Allergen profile, claims implications and label impact follow from every ingredient choice, so the scope should say whether the scientist is responsible for flagging them.",
      "Many independent food scientists are employed full time elsewhere in the same category, which creates both a conflict question and a confidentiality question in both directions.",
      "Shelf-life and stability work is a separate engagement with separate cost; do not assume a formulation fee includes it.",
    ],
    founderMistakes: [
      "Paying a food scientist by invoice with no written agreement, and discovering during diligence that nothing assigned the formula to the company.",
      "Engaging someone employed by a large manufacturer without asking about their employment obligations, then facing a claim from that employer.",
      "Accepting a bench formula that used an ingredient available only in pallet quantities from a supplier with a high minimum.",
      "Never securing scale-up support, then paying a second developer to redo the work when the first plant trial separated.",
      "Losing access to the bench records when the scientist moved on, leaving nobody able to explain why a stabiliser was chosen.",
    ],
    negotiationPoints: [
      "Require a present assignment of the formula, specification, process parameters and bench records, signed at engagement.",
      "Ask for a written representation that they are free of conflicting employment or client obligations.",
      "Define the deliverable as a costed, sourced formula with process parameters, not a bench recipe.",
      "Include a set number of scale-up and pilot-run attendance days in the fee.",
      "Agree post-engagement reformulation rates and a response-time commitment.",
      "Add non-use covering their other clients in your category for a defined period.",
    ],
    redFlags: [
      "No written assignment of the formula anywhere in the engagement.",
      "A scientist unwilling to confirm they are free of employer obligations.",
      "Deliverables defined as a recipe with no supplier names or usage rates.",
      "A retained right to reuse the formulation approach for other clients.",
      "No obligation to support the first plant trial.",
    ],
    beforeYouSign: [
      "The scientist's written confirmation of any current employer or client conflicts.",
      "A deliverables list naming the costed formula, the specification, the process parameters and the bench records.",
      "Agreement on who pays for ingredient samples and bench trials.",
    ],
    related: [
      "formula-development-agreement",
      "formula-assignment-agreement",
      "product-development-nda",
      "beverage-formulator-agreement",
      "consultant-agreement",
      "product-development-lab-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "beverage-formulator-agreement",
    name: "Beverage Formulator Agreement",
    category: "product-development",
    subcategory: "Development talent",
    aliases: ["Drink Formulator Agreement", "Beverage Development Agreement", "Liquid Formulation Agreement"],
    stages: ["idea", "development", "pre-launch"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Beverage Formulator or Development House",
    whenYouNeedIt:
      "When developing any drinkable product — functional beverage, energy drink, ready-to-drink coffee or tea, sports hydration, juice, kombucha, protein shake — where the liquid has to survive a thermal process and a shelf life.",
    whatItDoes:
      "Covers the formulation of the liquid together with the process conditions it needs, and allocates responsibility for the technical filings and validations a beverage requires before a plant will run it.",
    whyItMatters:
      "Beverage development is where founders most often discover that a formula is not a product. A drink that tastes right at the bench can separate at scale, lose flavour through pasteurisation, or fall into a process category the chosen plant cannot run. The regulatory and process side — acidification, thermal validation, water activity, the process authority letter a co-packer will ask for — is real work with real cost, and if the agreement does not say who does it, it lands on the founder in the week before a scheduled production slot.",
    majorTerms: [
      "Target sensory, nutritional and cost profile",
      "Number of formulation rounds and sample deliveries",
      "Process conditions the formula is designed for",
      "Responsibility for process authority review and thermal validation",
      "Responsibility for stability and shelf-life work",
      "Ownership of the formula, process parameters and modifications",
      "Ingredient sourcing and supplier qualification",
      "Scale-up support and pilot attendance",
      "Reformulation obligations if a plant trial fails",
      "Fee structure and payment milestones",
    ],
    clausesToWatch: [
      {
        clause: "Definition of a successful deliverable",
        why: "Without acceptance criteria tied to a plant trial, a formulator can be paid in full for a liquid that has never been made outside a bench.",
      },
      {
        clause: "Responsibility for process filings and validation",
        why: "If nobody is named, this cost and lead time appear late and stall a booked production run.",
      },
      {
        clause: "Who pays for a failed trial",
        why: "Plant time and materials for a failed run are expensive, and silence means the brand pays every time.",
      },
      {
        clause: "Ownership of process parameters",
        why: "Owning the ingredient list without the times, temperatures and pressures leaves you unable to hand the product to another plant.",
      },
      {
        clause: "Shelf-life responsibility",
        why: "A formulator who is not responsible for stability can deliver a formula that meets sensory targets and fails at month four.",
      },
      {
        clause: "Reformulation on ingredient substitution",
        why: "Supply disruptions force substitutions; without an obligation and a rate, each one becomes a new negotiation.",
      },
    ],
    cpgIssues: [
      "Thermal process, acidification and water activity decide which plants can even run the product, so the formulator should be told the target facility before the formula is finalised.",
      "A co-packer will typically require a process authority letter or equivalent technical sign-off before scheduling a run, and it has a lead time.",
      "Carbonation volumes, fill temperature and headspace interact with the formula and with the can or bottle specification.",
      "Flavour fade through pasteurisation is a common failure that only shows up after a real thermal treatment, not at the bench.",
      "Preservative systems and pH targets change with the pack format, so the same liquid in a can and a PET bottle can be two formulations.",
      "Sweetener systems, botanical extracts and functional actives all carry sourcing, cost and label consequences that need naming during development, not after.",
      "Fill volume and net-content tolerance are specification issues the formulator's work has to accommodate.",
    ],
    founderMistakes: [
      "Approving a bench sample and booking a production slot before any pilot run, then losing the slot and the deposit when the liquid failed at scale.",
      "Assuming the formulator would handle the process authority review, and finding out weeks before production that nobody had started it.",
      "Signing off a formula that tasted right cold but lost its top notes after pasteurisation.",
      "Developing to a target cost that ignored the minimum order quantity of the botanical extract at the centre of the formula.",
      "Building the formula for a bottle and then switching to cans without reopening the preservation and carbonation work.",
    ],
    negotiationPoints: [
      "Tie final acceptance and final payment to a successful pilot run at a named plant, not to a bench sample.",
      "Name who is responsible for process authority review, thermal validation and the associated cost.",
      "Ask for a defined number of reformulation rounds after the first failed plant trial, included in the fee.",
      "Require ownership of process parameters and equipment assumptions alongside the formula.",
      "Get shelf-life and stability responsibility written down, whoever holds it.",
      "Ask for supplier alternatives to be identified for every critical ingredient.",
    ],
    redFlags: [
      "Acceptance based on bench samples only.",
      "Silence on process authority, thermal validation or stability responsibility.",
      "Deliverables that omit process parameters.",
      "No obligation to support the first commercial run.",
      "A formula built around a single-source ingredient with no alternative identified.",
    ],
    beforeYouSign: [
      "The target plant identified, with its process capabilities and pack formats confirmed.",
      "A written development brief covering cost, sensory targets, claims ambitions and pack format.",
      "Agreement on who pays for pilot materials and plant time.",
    ],
    related: [
      "formula-development-agreement",
      "pilot-production-agreement",
      "stability-testing-agreement",
      "shelf-life-testing-agreement",
      "flavor-house-agreement",
      "co-packer-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "product-development-lab-agreement",
    name: "Product Development Laboratory Agreement",
    category: "product-development",
    subcategory: "Development partners",
    aliases: ["Lab Services Agreement", "Development Lab Agreement", "Contract Lab Agreement"],
    stages: ["development", "pre-launch"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Development Laboratory",
    whenYouNeedIt:
      "When engaging a laboratory or development house that has its own technology platform, standing formulation library or bench facility, rather than an individual consultant.",
    whatItDoes:
      "Governs the lab's development services and, critically, separates the lab's pre-existing platform technology from the formula built for you, then licenses whichever parts of the platform your formula needs in order to be made.",
    whyItMatters:
      "The reuse question is the whole negotiation with a lab. Development houses build on standing platforms — an emulsion system, a masking technology, a stable protein base — and they will not assign that platform, nor should you expect them to. What matters is whether your formula is yours, whether the lab can hand a near-identical formula to the next brand that walks in, and whether the licence to the platform is broad enough that a co-manufacturer can legally produce your product. Founders negotiate ownership hard and forget the licence, and end up owning a formula nobody is permitted to make.",
    majorTerms: [
      "Scope of services and development milestones",
      "Dated schedule of the lab's background technology",
      "Assignment of the formula developed for you",
      "Licence to background technology embedded in the formula",
      "Whether the licence is sublicensable to your manufacturers",
      "Restriction on the lab reusing your formula for other clients",
      "Exclusivity window in your category, if any",
      "Ownership of analytical methods and test data",
      "Sample and bench-record handling",
      "Fees, milestones and acceptance criteria",
    ],
    clausesToWatch: [
      {
        clause: "Reuse of the developed formula",
        why: "Without an express restriction, the lab can offer a very close variant of your formula to the next brand in your category.",
      },
      {
        clause: "Background technology schedule",
        why: "An undated or open-ended schedule lets the lab reclassify project output as pre-existing platform after the project succeeds.",
      },
      {
        clause: "Sublicence to manufacturers",
        why: "A licence that does not reach third-party plants means your own co-packer has no right to use the technology your formula depends on.",
      },
      {
        clause: "Analytical methods and specifications",
        why: "The methods used to test the product need to travel with it, or every future lot test starts from scratch.",
      },
      {
        clause: "Acceptance criteria",
        why: "Milestone payments with no objective acceptance standard turn every review round into a fee discussion.",
      },
      {
        clause: "Category exclusivity",
        why: "A lab serving four brands in one category is normal; whether that includes your specific brief is what needs settling.",
      },
    ],
    cpgIssues: [
      "Labs commonly hold genuine platform IP — encapsulation, emulsion stability, bitterness masking, shelf-stable protein systems — and demanding ownership of the platform ends the conversation without improving your position.",
      "Bench-scale equipment does not represent plant conditions, so a lab deliverable should be explicit about what has and has not been demonstrated at scale.",
      "Labs frequently propose ingredients from suppliers they have relationships with, which can be a benefit or a lock-in depending on the terms.",
      "Analytical methods developed for your product determine what a co-manufacturer's QA team can even test against.",
      "Retained samples and stability sets held at the lab are physical copies of your product and need a retention and destruction schedule.",
      "Where the lab also sells ingredients or premixes, development advice and commercial interest overlap, and that should be disclosed.",
    ],
    founderMistakes: [
      "Paying for a full development programme and receiving a formula the lab was free to adapt for a competitor three months later.",
      "Securing assignment of the formula but no sublicence rights, so the co-packer could not legally run it without a further deal with the lab.",
      "Accepting a background schedule marked 'to be provided', which arrived after success and covered the core of the solution.",
      "Leaving analytical methods with the lab, which meant every lot test had to go back to them at their rates.",
    ],
    negotiationPoints: [
      "Ask for assignment of the project formula plus a perpetual, worldwide, transferable, sublicensable licence to any embedded background technology.",
      "Ask for an express restriction on the lab supplying the same or a substantially similar formula to others in your category.",
      "Require the background schedule dated and signed before the first bench trial.",
      "Ask for analytical methods, specifications and stability data to be delivered as part of the deliverable.",
      "Negotiate a defined exclusivity window on the specific brief rather than a broad category non-compete.",
      "Tie milestone payments to written acceptance criteria agreed in advance.",
    ],
    redFlags: [
      "No restriction at all on the lab reusing your formula.",
      "A licence to background technology that ends when the services end.",
      "A background schedule left blank or deferred.",
      "Consent required to transfer the licence on a sale of the business.",
      "Analytical methods retained by the lab with no delivery obligation.",
    ],
    beforeYouSign: [
      "The dated background technology schedule.",
      "A written answer to whether a different plant could make this product using only what you will hold.",
      "The lab's disclosure of any ingredient or premix it sells that appears in your formula.",
    ],
    related: [
      "product-development-agreement",
      "rd-agreement",
      "product-development-ip-agreement",
      "formula-development-agreement",
      "stability-testing-agreement",
      "product-development-nda",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "flavor-house-agreement",
    name: "Flavor House Agreement",
    category: "product-development",
    subcategory: "Development partners",
    aliases: ["Flavour House Agreement", "Flavor Supply and Development Agreement", "Flavor Development Agreement"],
    stages: ["development", "pre-launch", "manufacturing", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Flavor House",
    whenYouNeedIt:
      "As soon as a flavor house begins developing a custom flavor for you — usually well before you are thinking about supply terms, which is exactly the point at which the supply terms are negotiable.",
    whatItDoes:
      "Covers both halves of the relationship: the development of a custom flavor system, and the ongoing supply of that flavor to you or to your manufacturer as an ingredient with its own price, lead time and minimums.",
    whyItMatters:
      "Flavor houses commonly retain ownership of the flavor system they develop and sell it to you as an ingredient. That is a legitimate and near-universal model, not a trap — it is how the industry works, and it is why custom development is often done at little or no upfront cost. But it means you do not own that layer of your product and you cannot move it. Your formula names a flavor code. Only one company can supply it. If they discontinue it, raise the price, impose a minimum you cannot meet, or decide your volume is not worth serving, your product stops. The negotiation is not about winning ownership; it is about what happens to you in each of those situations.",
    majorTerms: [
      "Ownership of the flavor system and of the flavor code",
      "Development fees, or the volume commitment given in exchange for free development",
      "Price per kilogram and the price-increase mechanism",
      "Minimum order quantity and minimum annual purchase",
      "Lead times and safety stock",
      "Whether the flavor is exclusive to you and for how long",
      "Whether they may sell the same or a similar flavor to others",
      "Discontinuation notice and transfer rights",
      "Specification escrow or deposit arrangements",
      "Right to have the flavor manufactured by a second source",
      "Sale of the flavor to your co-manufacturer versus to you directly",
      "Confidentiality and non-use of your brief and target profile",
    ],
    clausesToWatch: [
      {
        clause: "Ownership of the flavor system",
        why: "If they own it, your formula contains a component only they can supply, and every other term in the agreement is downstream of that fact.",
      },
      {
        clause: "Exclusivity and its duration",
        why: "Development done at no charge is usually paid for with exclusivity you do not get, or with a volume commitment you may not hit.",
      },
      {
        clause: "Minimum order quantity and annual minimums",
        why: "Flavor minimums are set for larger customers; an early-stage brand can be locked into buying more concentrate than it can use before the flavor's own shelf life expires.",
      },
      {
        clause: "Discontinuation rights",
        why: "A flavor house that stops making a low-volume flavor can end your SKU, and without a transfer or last-time-buy right you have no path forward.",
      },
      {
        clause: "Price increases",
        why: "With a single-source ingredient there is no competing quote to hold pricing, so the increase mechanism is the only protection you have.",
      },
      {
        clause: "Sale to a competitor",
        why: "Without a restriction, the same flavor profile can be offered to the next brand in your category, sometimes under a different code.",
      },
      {
        clause: "Supply to your manufacturer",
        why: "If the flavor is sold to your co-packer rather than to you, the relationship — and the leverage — sits between them, not with you.",
      },
    ],
    cpgIssues: [
      "A formula listing 'Flavor 4471 at 0.18%' is not a transferable formula; it is a pointer to another company's property, which is why formula ownership and flavor ownership are separate questions.",
      "Compounded flavors carry their own shelf life, so a minimum order sized for a larger brand can expire in your co-packer's cooler before you use it.",
      "Flavor houses hold the natural and artificial characterisation of the flavor, which drives your ingredient statement and any clean-label positioning you have built.",
      "Allergen and certification status of the flavor system — kosher, halal, organic-compliant, non-GMO — flows straight through to your label claims and your retailer requirements.",
      "Lead times on custom flavors are long relative to a co-packer's production schedule, and a missed flavor delivery cancels a run.",
      "Where the co-manufacturer buys the flavor on your behalf, you may never see the true price or the terms attached to it.",
      "Matching a flavor at a second house is possible but is a development project in itself, with its own timeline and sensory risk.",
    ],
    founderMistakes: [
      "Taking free development and only later discovering it was tied to an annual minimum purchase the brand could not reach.",
      "Building a whole product around a custom flavor with no discontinuation notice, then losing the SKU when the house rationalised its low-volume codes.",
      "Never asking whether the flavor was exclusive, and finding a competitor launching a strikingly similar profile.",
      "Letting the co-packer own the flavor relationship, which left the brand without pricing visibility or a way to move production.",
      "Ordering a full minimum of flavor concentrate that expired before it could be used, because the flavor's shelf life was shorter than the purchase cycle.",
      "Assuming the flavor house's ownership of the flavor also meant they owned the finished product formula, and negotiating nothing at all.",
    ],
    negotiationPoints: [
      "Accept that ownership of the flavor system is usually not available, and negotiate the consequences instead.",
      "Ask for a non-exclusive supply commitment: a written obligation to continue supplying at agreed terms for a defined period, with a notice requirement before any change.",
      "Ask for transfer rights on discontinuation — if they stop making the flavor, you receive the specification or the right to have it made elsewhere.",
      "Ask for specification escrow held by a third party and released on defined triggers such as discontinuation, insolvency or persistent failure to supply.",
      "Ask for second-source rights: permission to have the flavor manufactured by another house if supply fails for a stated period.",
      "Negotiate a last-time-buy right and a minimum notice period before discontinuation or a material price increase.",
      "Ask for category exclusivity for a defined window in exchange for a realistic volume commitment, and size the commitment to your actual forecast.",
      "Buy the flavor directly rather than through the co-packer, so the relationship and the pricing sit with you.",
    ],
    redFlags: [
      "An annual minimum purchase set well above your realistic forecast.",
      "No notice period before discontinuation.",
      "No restriction on selling the same profile into your category.",
      "Unlimited price-increase rights on a single-source ingredient.",
      "A flavor supplied only through your co-manufacturer with no direct contract between you and the house.",
      "Development described as free with no written statement of what is given in return.",
      "Refusal to discuss any escrow, transfer or second-source arrangement.",
    ],
    beforeYouSign: [
      "Your realistic twelve-month volume forecast, so any minimum can be tested against it rather than accepted on optimism.",
      "The flavor's own shelf life and the minimum order quantity, compared against your production cycle.",
      "Written confirmation of the flavor's allergen, certification and label characterisation status.",
      "A written answer to a single question: if this flavor became unavailable in six months, what would you do?",
    ],
    related: [
      "formula-ownership-agreement",
      "ingredient-supplier-development-agreement",
      "formula-development-agreement",
      "ingredient-supply-agreement",
      "non-disclosure-non-use-agreement",
      "product-development-ip-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "This agreement usually leaves a component of your product owned and single-sourced by someone else, so the supply, discontinuation and escrow terms are the ones carrying the business risk.",
    templateId: "flavor-house-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "ingredient-supplier-development-agreement",
    name: "Ingredient Supplier Development Agreement",
    category: "product-development",
    subcategory: "Development partners",
    aliases: ["Supplier Co-Development Agreement", "Ingredient Development Partnership", "Joint Ingredient Development Agreement"],
    stages: ["development", "pre-launch", "manufacturing"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Ingredient Supplier",
    whenYouNeedIt:
      "When an ingredient supplier offers to help develop your product — running applications work, providing free samples, or building a custom blend — usually in exchange for the resulting business.",
    whatItDoes:
      "Sets the terms of supplier-funded development work and what the supplier receives in return: supply rights, purchase commitments, exclusivity, or ownership of the blend they created.",
    whyItMatters:
      "Applications support from an ingredient supplier is genuinely useful and genuinely not free. The price is usually paid in ingredient exclusivity, a minimum purchase, or a formula built so specifically around their material that switching means reformulating. None of that is unreasonable — but founders accept it without pricing it, then find their landed cost has no competitive tension in it. When the supplier raises price, there is no second quote to hold against them because the formula only works with their ingredient.",
    majorTerms: [
      "Scope of the development support provided",
      "What the supplier receives: exclusivity, volume commitment, or supply rights",
      "Ownership of any custom blend or premix created",
      "Ingredient exclusivity — whether you must buy solely from them",
      "Minimum purchase volumes and the period they run for",
      "Pricing and the price-increase mechanism",
      "Right to qualify a second source",
      "Specification and COA obligations",
      "Confidentiality of your formula and target profile",
      "Term and exit rights",
    ],
    clausesToWatch: [
      {
        clause: "Ingredient exclusivity",
        why: "An obligation to buy the ingredient only from them removes your ability to benchmark price for the life of the product.",
      },
      {
        clause: "Ownership of the custom blend",
        why: "A supplier-owned premix behaves like a flavor code — you cannot move it, and your formula depends on it.",
      },
      {
        clause: "Minimum purchase commitments",
        why: "Volume commitments made during development are based on a forecast that has never been tested by an actual sales year.",
      },
      {
        clause: "Price mechanism",
        why: "Without a defined mechanism, a single-source ingredient can be repriced at will once your product is on shelf.",
      },
      {
        clause: "Second-source qualification",
        why: "If the agreement blocks you from qualifying an alternative, a single supply failure becomes a stockout with no remedy.",
      },
    ],
    cpgIssues: [
      "Free applications work usually specifies the supplier's own material at a usage rate that suits them, which builds a switching cost into the formula from the first bench trial.",
      "Custom premixes and blends carry their own minimum order quantities and shelf lives, often longer lead times than the base ingredients they replace.",
      "Ingredient certifications — organic, non-GMO, kosher, allergen status — flow directly to your label, so a supplier change is a label change.",
      "COA specifications and testing frequency differ between suppliers, and a second source has to be qualified against your specification before it can be used.",
      "Suppliers often want to publish your brand as an application case study, which tells competitors what is in your product.",
      "Ingredient price volatility in agricultural inputs is real, and a fixed-price commitment can cut either way depending on the term.",
    ],
    founderMistakes: [
      "Accepting free applications support and an exclusive supply obligation in the same conversation, without treating the second as the price of the first.",
      "Committing to an annual volume during development that turned out to be several times the first year's actual sales.",
      "Building the formula on a supplier-owned premix, which made moving suppliers a reformulation project rather than a purchasing decision.",
      "Letting the supplier publish a case study naming the brand, which handed a competitor the ingredient and usage approach.",
    ],
    negotiationPoints: [
      "Price the development support explicitly, then decide whether exclusivity is worth it rather than accepting it as a courtesy.",
      "Keep the right to qualify a second source even if you commit to buy primarily from them.",
      "Size any volume commitment to a conservative forecast and ask for a shortfall remedy other than a penalty.",
      "Ask for ownership of any custom blend, or for escrow and transfer rights if ownership is not available.",
      "Require written consent before any use of your brand name in their marketing or case studies.",
      "Negotiate a defined price mechanism tied to documented input costs.",
    ],
    redFlags: [
      "Exclusive purchase obligations with no term limit or volume cap.",
      "Minimum commitments set during development with no adjustment mechanism.",
      "A prohibition on qualifying an alternative supplier.",
      "Supplier ownership of a premix with no transfer or escrow provision.",
      "A right to name your brand publicly as a customer without consent.",
    ],
    beforeYouSign: [
      "A landed-cost model showing what the ingredient costs at your real volumes, not at the volume in the proposal.",
      "Confirmation of whether a comparable ingredient exists from another supplier, and what qualifying it would take.",
    ],
    related: [
      "ingredient-supply-agreement",
      "flavor-house-agreement",
      "raw-material-supply-agreement",
      "supplier-nda",
      "master-supply-agreement",
      "formula-development-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "product-development-consultant-agreement",
    name: "Product Development Consultant Agreement",
    category: "product-development",
    subcategory: "Development talent",
    aliases: ["CPG Consultant Agreement", "Development Advisor Agreement", "Product Consultant Engagement"],
    stages: ["idea", "development", "pre-launch"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Independent Consultant",
    whenYouNeedIt:
      "When bringing in a generalist who will guide the whole development process — sourcing, co-packer selection, specification, regulatory sequencing — rather than doing the formulation themselves.",
    whatItDoes:
      "Defines the consultant's scope, deliverables and fee, sorts out ownership of what they produce, and addresses the conflicts that come with someone who works across several brands in your space.",
    whyItMatters:
      "A good development consultant brings a network — co-packers, flavor houses, ingredient suppliers, brokers — and that network is simultaneously the value and the risk. The relationships they introduce you to are usually theirs, they are often working with two or three other brands at the same time, and the brief you give them is a competitive document. The agreement has to secure your ownership of the output, restrict use of your brief, and be honest about which introductions come with a referral fee.",
    majorTerms: [
      "Scope of work and specific deliverables",
      "Fee structure: retainer, day rate, project or milestone",
      "Ownership of documents, specifications and models produced",
      "Disclosure of referral fees and supplier relationships",
      "Non-use of your brief and commercial information",
      "Restrictions on concurrent work with directly competing brands",
      "Introductions and whether relationships are yours to keep",
      "Confidentiality with your suppliers and manufacturers",
      "Term, notice and handover obligations",
      "Whether any equity or success fee applies",
    ],
    clausesToWatch: [
      {
        clause: "Referral fee disclosure",
        why: "A consultant paid by the co-packer they recommend has an interest that is not aligned with yours, and it needs to be visible.",
      },
      {
        clause: "Ownership of work product",
        why: "Cost models, specifications and vendor comparisons built for you should be yours, not reusable templates that leave with them.",
      },
      {
        clause: "Concurrent competing engagements",
        why: "A consultant running your brief and a competitor's in the same month can legitimately apply what they learned unless use is restricted.",
      },
      {
        clause: "Handover obligations",
        why: "Without a handover clause, the departure of a consultant takes the vendor relationships and the reasoning with them.",
      },
      {
        clause: "Success fees and equity",
        why: "Equity or success-based compensation changes the advice you get and needs its own documentation.",
      },
    ],
    cpgIssues: [
      "Co-packer introductions are the single most valuable thing many consultants provide, and whether that relationship stays with you after they leave is worth writing down.",
      "Consultants often hold their own confidentiality obligations to the co-packers and suppliers they work with, which limits what they can tell you.",
      "Regulatory sequencing — facility registration, label review, claims substantiation, certification timelines — is commonly assumed to be in scope and commonly is not.",
      "Cost models built by a consultant carry assumptions about MOQs, freight and yield that need to be visible and owned by you.",
      "A consultant working across several brands sees comparative co-packer pricing, which is confidential to those plants.",
      "Trade show and category introductions carry commercial value and sometimes commission arrangements that should be disclosed.",
    ],
    founderMistakes: [
      "Paying a consultant who was also collecting a fee from the recommended co-packer, with no disclosure in the agreement.",
      "Losing the supplier relationships when the consultant moved on because every contact ran through their email.",
      "Assuming regulatory and label review was in scope, and discovering weeks before launch that nobody had done it.",
      "Sharing a full commercial brief with a consultant who was concurrently advising a directly competing launch.",
    ],
    negotiationPoints: [
      "Require written disclosure of every referral fee, commission or supplier relationship before an introduction is made.",
      "Ask for ownership of all work product including models, specifications and vendor evaluations.",
      "Ask that supplier and co-packer relationships be introduced directly to the company, with your own contract and contacts.",
      "Restrict use of your brief and commercial data across their other clients for a defined period.",
      "Define the regulatory scope explicitly: what is in, what is out, and who does the rest.",
    ],
    redFlags: [
      "Refusal to disclose referral or commission arrangements.",
      "Work product treated as the consultant's reusable methodology.",
      "No restriction on concurrent engagements in your exact category.",
      "Vendor relationships held only through the consultant.",
      "Scope described in outcomes with no deliverables list.",
    ],
    beforeYouSign: [
      "A deliverables list with dates, not a description of activity.",
      "Written disclosure of current clients in your category, to whatever extent their own confidentiality allows.",
    ],
    related: [
      "consultant-agreement",
      "product-development-agreement",
      "independent-contractor-agreement",
      "product-development-nda",
      "food-scientist-agreement",
      "advisor-agreement",
    ],
    attorneyReview: "depends",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "rd-agreement",
    name: "R&D Agreement",
    category: "product-development",
    subcategory: "Development programmes",
    aliases: ["Research and Development Agreement", "Joint Development Agreement", "Sponsored Research Agreement"],
    stages: ["development", "growth"],
    importance: "advanced",
    entityTypes: ["any"],
    parties: "Brand + Research Partner (institution, lab or corporate partner)",
    whenYouNeedIt:
      "For a genuine research programme rather than a formulation project: a novel ingredient, a process innovation, a clinical or sensory study, or joint work with a university or a larger corporate partner.",
    whatItDoes:
      "Governs a funded research relationship — the programme plan, the funding, the milestones, and above all the allocation of rights in whatever the research produces, including patentable output.",
    whyItMatters:
      "R&D agreements fail on the back end, not the front. The programme runs, something useful comes out of it, and the parties then discover they disagreed all along about who owns it, whether the partner may publish it, and whether the brand may commercialise it without a further licence. Institutions in particular have standing positions on publication and on retained research rights, and those positions are non-negotiable in some places. Sort the ownership, the publication timing and the commercialisation licence before the first dollar moves.",
    majorTerms: [
      "Research plan, milestones and reporting",
      "Funding schedule and what it covers",
      "Background IP schedules from both sides",
      "Ownership of results, including any patentable output",
      "Commercialisation licence and its scope",
      "Publication rights and review windows",
      "Retained research rights of an institutional partner",
      "Confidentiality and its interaction with publication",
      "Inventorship and patent prosecution responsibility",
      "Termination and what happens to work in progress",
    ],
    clausesToWatch: [
      {
        clause: "Publication rights",
        why: "A partner free to publish can put your development approach into the public domain before you have filed or launched.",
      },
      {
        clause: "Ownership of jointly created results",
        why: "Joint ownership sounds fair and is operationally difficult, since each owner's rights to exploit or license differ by jurisdiction.",
      },
      {
        clause: "Commercialisation licence scope",
        why: "A licence limited by field or territory can block the line extension or the export market you build the programme for.",
      },
      {
        clause: "Retained research rights",
        why: "An institution's right to keep using results for its own research is standard, but the boundary with commercial use needs defining.",
      },
      {
        clause: "Patent prosecution and cost",
        why: "Filing and maintaining patents is expensive and long-running, and silence on who pays creates a stalemate at the worst time.",
      },
      {
        clause: "Termination and work in progress",
        why: "Programmes end early, and without a clause the partly finished results sit in an undefined state.",
      },
    ],
    cpgIssues: [
      "Novel ingredient work can carry a regulatory pathway with its own timeline and cost, and the agreement should name who is responsible for it.",
      "Clinical or consumer studies intended to support a product claim need their protocols and data ownership settled before recruitment starts, since claims substantiation is the whole point.",
      "Sensory and consumer research data is commercially valuable to a corporate research partner in its own right.",
      "Publication timelines at institutions can conflict with a launch window, so a review-and-delay mechanism matters more than an outright ban.",
      "Process innovations developed with a manufacturer can end up embedded in that manufacturer's equipment and practice, which complicates moving production later.",
      "Ingredient suppliers funding research on their own material will want the results, and that can conflict with your exclusivity.",
    ],
    founderMistakes: [
      "Funding a study whose data the partner owned, leaving the brand unable to use the results to support a claim.",
      "Agreeing joint ownership of results and then finding both parties needed the other's consent to do anything with them.",
      "Missing the publication clause and seeing the approach presented at a conference before launch.",
      "Never settling who pays for patent filing, which meant nothing was filed at all.",
    ],
    negotiationPoints: [
      "Ask for sole ownership of results in your field of use, with the partner retaining rights outside it.",
      "Negotiate a publication review window long enough to file or launch, rather than attempting a permanent restriction.",
      "Define the commercialisation licence as perpetual, worldwide and sublicensable to your manufacturers.",
      "Agree who funds patent prosecution and who controls it before any filing decision arises.",
      "Set data ownership for any study intended to support a claim, and confirm you can use the results in marketing.",
    ],
    redFlags: [
      "Partner ownership of results with only a narrow licence back to you.",
      "Unrestricted publication rights with no review period.",
      "Joint ownership with no operating agreement for how it works in practice.",
      "No allocation of patent costs.",
      "Study data owned by the partner where the purpose was claims substantiation.",
    ],
    beforeYouSign: [
      "A written research plan with milestones and defined deliverables.",
      "Both background IP schedules, dated and signed.",
      "A clear statement of what commercial use you need to make of the results.",
    ],
    related: [
      "product-development-ip-agreement",
      "product-development-lab-agreement",
      "product-development-agreement",
      "sensory-testing-agreement",
      "formula-development-agreement",
      "mutual-nda",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Ownership, publication and patent rights in a funded research programme are technical and hard to renegotiate once results exist, and institutional partners often have fixed positions.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "formula-development-agreement",
    name: "Formula Development Agreement",
    category: "product-development",
    subcategory: "Formula agreements",
    aliases: ["Formulation Development Agreement", "Recipe Development Agreement", "Formula Creation Agreement"],
    stages: ["development", "pre-launch"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Formulator, Lab or Development Partner",
    whenYouNeedIt:
      "Before formulation work starts, whoever is doing it — an individual scientist, a lab, a co-manufacturer's in-house team, or a flavor house extending into full formulation.",
    whatItDoes:
      "Commissions the creation of a formula: what it must achieve, how many rounds, what gets delivered, what acceptance means, and who owns the result and every later modification of it.",
    whyItMatters:
      "The deliverable is the whole argument. A formula delivered as a bench recipe with percentages is not the same asset as a formula delivered with named suppliers, usage rates, process parameters, analytical methods and a cost per unit at stated volumes. The first one cannot be handed to a second manufacturer; the second one can. Founders pay development fees and receive the first version, then find that the thing they own is not the thing they need when they try to move plants or sell the business.",
    majorTerms: [
      "Target specification: sensory, nutritional, cost and claims",
      "Number of development rounds and sample deliveries",
      "Definition of the deliverable formula package",
      "Acceptance criteria and who signs off",
      "Present assignment of the formula and specification",
      "Ownership of modifications and improvements, whoever makes them",
      "Right to disclose the formula to alternative manufacturers",
      "Confidentiality and non-use across the developer's other clients",
      "Fees, milestones and payment triggers",
      "Scale-up support and post-delivery obligations",
    ],
    clausesToWatch: [
      {
        clause: "Definition of the delivered formula package",
        why: "A formula without process parameters, supplier names and usage rates is not transferable to another plant.",
      },
      {
        clause: "Ownership of modifications",
        why: "The version you sell is usually not the version delivered; if modifications sit with the developer, the commercial product does too.",
      },
      {
        clause: "Right to take the formula elsewhere",
        why: "An express right to disclose the formula to another manufacturer under confidentiality prevents the argument you will otherwise have at exit.",
      },
      {
        clause: "Acceptance criteria",
        why: "Without objective criteria, the developer decides when they are finished and you decide when to pay, and those two moments do not coincide.",
      },
      {
        clause: "Non-use across other clients",
        why: "A developer free to reuse your formulation approach can deliver a close cousin of your product to a competitor.",
      },
      {
        clause: "Costed formula obligation",
        why: "A formula that meets sensory targets but misses the cost target is a failed deliverable dressed as a successful one.",
      },
    ],
    cpgIssues: [
      "Ask for the formula delivered with named suppliers, part numbers and usage rates, because a percentage list alone leaves a new plant guessing.",
      "Process parameters — order of addition, shear, temperature, hold times, fill conditions — are as much the product as the ingredients are.",
      "The costed formula should be built at your actual volumes, since ingredient pricing at pallet quantities bears little relation to pricing at a first-run volume.",
      "Label impact should be part of the deliverable: ingredient statement order, allergen declarations, and whether the formula supports the claims the brand intends to make.",
      "Ingredient minimums and lead times determine whether a formula is producible at all, and belong in the acceptance discussion.",
      "Shelf-life and stability validation is a separate scope with a separate timeline; a formula is not accepted until it holds.",
    ],
    founderMistakes: [
      "Accepting delivery of a percentage-based recipe with no supplier names, which made the second co-packer quote guesswork.",
      "Paying the final milestone on a bench sample and having no leverage left when the plant trial failed.",
      "Leaving modifications unaddressed, so the reformulation that fixed a separation problem was owned by the developer.",
      "Developing to a cost target based on ingredient pricing at volumes the brand would not reach for three years.",
      "Never getting an express right to disclose the formula to another manufacturer, which turned a plant move into a negotiation.",
    ],
    negotiationPoints: [
      "Define the deliverable as a complete formula package: costed, sourced, with process parameters and analytical methods.",
      "Hold a meaningful final payment until acceptance at a pilot or first commercial run.",
      "Ask for present assignment of the formula and of all modifications, however and by whomever made.",
      "Include an express right to disclose the formula to any alternative manufacturer under confidentiality.",
      "Add non-use covering the developer's other clients in your category for a defined period.",
      "Agree a fixed number of post-delivery reformulation rounds within the original fee.",
    ],
    redFlags: [
      "Deliverables defined as 'the formula' with no further specification.",
      "Silence on ownership of modifications.",
      "No right to disclose the formula to another manufacturer.",
      "Full payment due on bench-sample approval.",
      "A developer retaining the right to reuse the formulation for other clients.",
    ],
    beforeYouSign: [
      "A written target specification covering sensory, cost, nutritional profile and intended claims.",
      "Agreement on what the delivered formula package contains, listed item by item.",
      "Agreement on what acceptance means and which run it is tested against.",
    ],
    related: [
      "formula-ownership-agreement",
      "formula-assignment-agreement",
      "product-development-agreement",
      "food-scientist-agreement",
      "flavor-house-agreement",
      "pilot-production-agreement",
    ],
    attorneyReview: "yes",
    templateId: "formula-development-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "product-development-agreement",
    name: "Product Development Services Agreement",
    category: "product-development",
    subcategory: "Development programmes",
    aliases: ["Development Services Agreement", "NPD Agreement", "Product Development Contract"],
    stages: ["development", "pre-launch"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Development Partner",
    whenYouNeedIt:
      "For any paid development engagement large enough to have phases: concept work, formulation, packaging development, pilot runs and a handover to manufacturing.",
    whatItDoes:
      "Acts as the umbrella agreement for the whole development programme — scope, phases, milestones, fees, acceptance, ownership and what happens when a phase fails or the project stops.",
    whyItMatters:
      "This is the document that decides whether a stalled project is recoverable. Development programmes stop for ordinary reasons: funding runs short, the co-packer changes, a sensory target proves unreachable. What matters then is whether you hold the work done to date, whether you can take it to someone else, and whether you owe for phases not delivered. Programmes papered as a proposal and an invoice leave all three questions open, and the answer defaults to whoever holds the files.",
    majorTerms: [
      "Phased scope with defined deliverables per phase",
      "Milestone fees and payment triggers",
      "Acceptance criteria and review periods",
      "Ownership of deliverables, including partial work",
      "Background and foreground IP allocation",
      "Change-control process and pricing for out-of-scope work",
      "Timeline, dependencies and delay consequences",
      "Termination for convenience and its consequences",
      "Handover package on completion or termination",
      "Confidentiality, non-use and category restrictions",
      "Warranties on the deliverable and remedy for failure",
    ],
    clausesToWatch: [
      {
        clause: "Ownership of partial work on termination",
        why: "Most programmes that stop, stop mid-phase, and without a clause the incomplete work stays with the developer.",
      },
      {
        clause: "Change control",
        why: "Development scope moves constantly, and an undefined change process turns every adjustment into an unbudgeted invoice.",
      },
      {
        clause: "Acceptance and deemed acceptance",
        why: "A short deemed-acceptance window means a deliverable you have not properly evaluated becomes accepted and payable.",
      },
      {
        clause: "Handover package",
        why: "Named specifically or not at all — this is the list that determines whether the next partner can pick up where this one stopped.",
      },
      {
        clause: "Remedy for a failed deliverable",
        why: "Without a rework obligation, a formula that fails at scale leaves you paying twice to fix it.",
      },
      {
        clause: "Termination for convenience",
        why: "A programme you cannot exit becomes a fixed cost long after the strategy behind it changed.",
      },
    ],
    cpgIssues: [
      "Development phases in CPG have hard external dependencies — a co-packer's trial slot, a flavor house lead time, a label print window — and delay clauses should reflect who controls each.",
      "Packaging development runs in parallel with formulation and has its own long lead items, so a single-track programme plan usually understates the timeline.",
      "Regulatory and label review is a distinct workstream that founders routinely assume is included; state whether it is.",
      "The handover package must be sufficient for a co-manufacturer to run the product: formula, specification, process parameters, analytical methods, stability data and artwork specification.",
      "Trial and pilot material costs are real money and are usually outside the development fee unless stated.",
      "A change in pack format or plant mid-programme is effectively a new formulation project, and change control should price that honestly.",
    ],
    founderMistakes: [
      "Running a six-figure development programme on a proposal, then having no basis to recover partial work when the project paused.",
      "Missing a deemed-acceptance clause and being treated as having accepted a formula they had not yet tested at scale.",
      "Assuming pilot materials and plant time were included in the development fee.",
      "Changing pack format mid-programme with no change control, and receiving an invoice larger than the original scope.",
      "Terminating a programme and receiving nothing beyond the last accepted deliverable, because the handover clause was never negotiated.",
    ],
    negotiationPoints: [
      "Ask for ownership of all work product, including partial and rejected work, on any termination.",
      "Define a handover package item by item and make it deliverable on termination as well as on completion.",
      "Set acceptance windows long enough to include an actual trial, not just a tasting.",
      "Negotiate a rework obligation at the developer's cost where a deliverable fails to meet the agreed specification.",
      "Include termination for convenience with payment for work performed to date and nothing beyond it.",
      "Agree change-control pricing mechanics before the first change request arrives.",
    ],
    redFlags: [
      "Ownership contingent on completion of the whole programme.",
      "Deemed acceptance in a short window with no trial.",
      "No rework remedy for a deliverable that fails at scale.",
      "No termination right, or termination with the full remaining fee payable.",
      "A handover obligation described only as 'reasonable assistance'.",
    ],
    beforeYouSign: [
      "A phased plan with deliverables, dates and dependencies, agreed by both sides.",
      "A written handover package list.",
      "Clarity on whether pilot materials, plant time and regulatory review are inside or outside the fee.",
    ],
    related: [
      "formula-development-agreement",
      "product-development-ip-agreement",
      "product-development-lab-agreement",
      "pilot-production-agreement",
      "product-development-nda",
      "product-development-consultant-agreement",
    ],
    attorneyReview: "yes",
    templateId: "product-development-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "formula-assignment-agreement",
    name: "Formula Ownership and Assignment Agreement",
    category: "product-development",
    subcategory: "Formula agreements",
    aliases: ["Formula Transfer Agreement", "Recipe Assignment", "Assignment of Formulation"],
    stages: ["development", "pre-launch", "manufacturing", "exit"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Formulator, Lab or Manufacturer + Brand",
    whenYouNeedIt:
      "When a formula already exists but the paperwork does not — work done before the company was formed, by a friend, by an individual who has since left, or by a developer who was never asked to assign anything.",
    whatItDoes:
      "Transfers ownership of an existing formula and its associated know-how, specifications, process parameters and records from whoever developed it to the company, and closes any gap in the chain of title.",
    whyItMatters:
      "This is the cleanup document, and almost every brand needs one eventually. The formula was developed by someone before there was a company to own it, or by a consultant paid by invoice, or by a co-packer's technologist as a favour. Everything works commercially until an investor or a buyer asks for the document that makes the formula the company's. Cleaning it up later means going back to people whose leverage has changed and whose interest in helping has usually declined.",
    majorTerms: [
      "Precise identification of the formula, version and date",
      "Present assignment of the formula, know-how and records",
      "Transfer of specifications, process parameters and analytical methods",
      "Assignment of all modifications made to date",
      "Consideration supporting the assignment",
      "Warranty that the assignor owns what they are transferring",
      "Representation that no third-party rights are embedded",
      "Delivery of bench records, trial data and retained samples",
      "Further assurances and continuing cooperation",
      "Confidentiality surviving the assignment",
    ],
    clausesToWatch: [
      {
        clause: "Warranty of ownership",
        why: "An assignor who developed the formula while employed elsewhere may not hold the rights they are transferring.",
      },
      {
        clause: "Version identification",
        why: "Assigning 'the formula' without a version and date leaves it unclear whether later improvements came across.",
      },
      {
        clause: "Embedded third-party components",
        why: "A formula containing a proprietary flavor or premix cannot be fully assigned, and the agreement should say so rather than imply otherwise.",
      },
      {
        clause: "Consideration",
        why: "An assignment signed years after the work with nothing given in exchange is the version most easily challenged later.",
      },
      {
        clause: "Delivery of records",
        why: "Ownership of a formula without the bench records and trial data leaves you owning a document rather than the knowledge behind it.",
      },
    ],
    cpgIssues: [
      "Formulas developed at a co-manufacturer by their staff often sit with the plant by default, and that assignment is far easier to obtain while you are a current customer than after you leave.",
      "Where a flavor system or premix is owned by a supplier, the assignment should carve it out and be paired with a supply or licence arrangement rather than overstating what transferred.",
      "The version that matters is the one currently in production, which is frequently several modifications away from the original development output.",
      "Process parameters held only in a plant's batch records need to be captured and transferred explicitly.",
      "Stability and shelf-life data supporting the current formula should travel with the assignment, since it is what a retailer and a new plant will ask for.",
      "Assignments from individuals should be paired with confirmation that no former employer has a claim, which is the most common defect in a first-product formula.",
    ],
    founderMistakes: [
      "Discovering during diligence that the formula had never been assigned by the friend who developed it, and having to negotiate for it under time pressure.",
      "Getting an assignment covering the original formula but not the three modifications made at the plant since.",
      "Assigning a formula that contained a supplier-owned flavor system without addressing the supply arrangement, so the transfer was incomplete in practice.",
      "Signing an assignment with no ownership warranty from someone who had developed the work while employed by a large manufacturer.",
      "Taking the assignment without the bench records, and having no way to explain a formulation choice when a substitution became necessary.",
    ],
    negotiationPoints: [
      "Identify the formula by version and date, and expressly include all modifications to date.",
      "Ask for delivery of bench records, trial data, analytical methods and any retained samples.",
      "Include a warranty of ownership plus a representation that no employer or third party has a claim.",
      "Carve out supplier-owned components honestly and pair them with the relevant supply terms.",
      "Provide real consideration and record it, particularly where the assignment is being signed long after the work.",
      "Include a further-assurances covenant, since recordation and confirmation requests can arise years later.",
    ],
    redFlags: [
      "An assignment with no ownership warranty.",
      "A formula described generally rather than by version.",
      "No delivery obligation for records, data or samples.",
      "Silence on modifications made since the original development.",
      "An assignor who declines to confirm they are free of employer obligations.",
    ],
    beforeYouSign: [
      "A written history of who touched the formula and when, from first bench trial to the current production version.",
      "The current production formula and specification in hand, matched against what is being assigned.",
      "A list of any supplier-owned components that cannot be assigned.",
    ],
    related: [
      "formula-ownership-agreement",
      "ip-assignment-agreement",
      "formula-development-agreement",
      "food-scientist-agreement",
      "recipe-formula-confidentiality-agreement",
      "co-packer-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "A late assignment closing a chain-of-title gap is exactly the document a buyer's counsel scrutinises, and defects in ownership warranties or consideration are hard to repair afterwards.",
    templateId: "formula-assignment-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "sample-testing-agreement",
    name: "Sample / Product Testing Agreement",
    category: "product-development",
    subcategory: "Testing and validation",
    aliases: ["Analytical Testing Agreement", "Third-Party Lab Testing Agreement", "Sample Analysis Agreement"],
    stages: ["development", "pre-launch", "manufacturing"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Testing Laboratory",
    whenYouNeedIt:
      "Whenever product goes to an outside laboratory for analysis: nutritional panel data, micro testing, heavy metals, potency of an active, allergen verification or a competitor benchmark.",
    whatItDoes:
      "Sets what will be tested, by which methods, to what turnaround, who owns the resulting data, and what the lab may and may not do with the samples you send.",
    whyItMatters:
      "Testing agreements look administrative and carry two real risks. The first is data ownership and use: a lab report is the evidence behind your nutrition panel and your claims, and you need to be able to give it to a retailer, an insurer or a buyer without asking permission. The second is the sample itself. Sending finished product to a lab is a disclosure of your formulation to an organisation that analyses food for a living, and unless the agreement restricts it, nothing prevents the analysis going further than the test you ordered.",
    majorTerms: [
      "Scope of testing and the specific methods used",
      "Turnaround times and rush provisions",
      "Ownership of test data and reports",
      "Right to share reports with retailers, insurers and buyers",
      "Sample handling, retention period and destruction",
      "Restriction on analysis beyond the ordered scope",
      "Confidentiality of results and of the sample itself",
      "Accreditation and method validation status",
      "Retest and dispute procedure",
      "Liability for erroneous results",
    ],
    clausesToWatch: [
      {
        clause: "Ownership and use of reports",
        why: "A lab that restricts onward disclosure of its report leaves you unable to hand it to the retailer or insurer who asked for it.",
      },
      {
        clause: "Restriction on further analysis",
        why: "Without it, a sample submitted for a micro test can lawfully be characterised more fully than you intended.",
      },
      {
        clause: "Sample retention and destruction",
        why: "Retained samples are physical copies of your product sitting in a third party's freezer with no end date.",
      },
      {
        clause: "Method specification",
        why: "Different methods give different numbers, and a result you cannot reproduce at a second lab is not usable evidence.",
      },
      {
        clause: "Liability for erroneous results",
        why: "Labs typically cap liability at the cost of the test, which is far below the cost of a label built on a wrong number.",
      },
    ],
    cpgIssues: [
      "Nutrition panel values come from lab analysis, so the method and the sampling plan behind those numbers matter for every label you print.",
      "Micro and pathogen testing on production lots is a release decision, and the turnaround directly affects when a co-packer can ship.",
      "Heavy metals, pesticide residue and contaminant testing on botanicals and proteins is increasingly demanded by retailers, and the specification varies by account.",
      "Potency testing for functional actives and supplements underpins label claims, and overage decisions depend on how the assay behaves across shelf life.",
      "Allergen verification after a changeover is a food-safety decision, not a development one, and belongs in the quality agreement as well.",
      "Competitor benchmark samples sent for analysis are someone else's product, and how you use those results is a separate question worth thinking about.",
      "COA formats and required parameters differ between retailers, distributors and insurers, so agree the report format before the first run.",
    ],
    founderMistakes: [
      "Building a nutrition panel on a single composite sample, then getting different values at a second lab and having no basis to reconcile them.",
      "Sending finished product for a simple test with no restriction on further analysis, to a lab that also serves competitors.",
      "Discovering that a lab report could not be shared with a retailer because of a restriction in the lab's standard terms.",
      "Ignoring sample retention entirely, leaving product with a lab indefinitely with no destruction obligation.",
    ],
    negotiationPoints: [
      "Ask for outright ownership of test data and an unrestricted right to share reports with retailers, insurers, investors and buyers.",
      "Add an express restriction on any analysis beyond the ordered scope, and on retaining data derived from your samples.",
      "Set a sample retention period with automatic destruction and certification.",
      "Specify methods by name and require notice before any method change.",
      "Agree the report format required by your largest retail customer before the first production lot.",
    ],
    redFlags: [
      "Lab terms restricting disclosure of your own results.",
      "No restriction on further characterisation of the sample.",
      "Indefinite sample retention.",
      "Method left to the lab's discretion on a test supporting a label value.",
      "No retest or dispute procedure for a result that will hold up a shipment.",
    ],
    beforeYouSign: [
      "A written testing plan naming methods, frequency and the sampling approach.",
      "Confirmation of what your retailers and your insurer require on a COA.",
    ],
    related: [
      "stability-testing-agreement",
      "shelf-life-testing-agreement",
      "quality-agreement",
      "product-specification-sheet",
      "supplier-quality-agreement",
      "pilot-production-agreement",
    ],
    attorneyReview: "depends",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "pilot-production-agreement",
    name: "Pilot Production Agreement",
    category: "product-development",
    subcategory: "Testing and validation",
    aliases: ["Trial Run Agreement", "Test Run Agreement", "Scale-Up Trial Agreement"],
    stages: ["development", "pre-launch", "manufacturing"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Manufacturer or Co-Packer",
    whenYouNeedIt:
      "Before the first run on real plant equipment — the trial that proves the bench formula survives commercial scale, and the run that decides whether the co-packer relationship works at all.",
    whatItDoes:
      "Covers a short production trial: what is being made, at whose cost, who supplies the materials, what counts as success, who owns the resulting data and process settings, and what happens to the product that comes off the line.",
    whyItMatters:
      "The pilot is where the money starts moving and where the first real arguments happen. Plant time, changeover, materials and destroyed product are all costs that appear whether or not the run works, and silence on who pays them means the brand pays them all. The second issue is quieter: the plant learns your process during the trial. Line settings, timings and adjustments developed in that run are part of how your product gets made, and if the agreement does not address them, they stay with the plant.",
    majorTerms: [
      "Trial scope, volume and number of attempts",
      "Cost allocation: plant time, changeover, labour and materials",
      "Who supplies ingredients and packaging for the trial",
      "Definition of a successful trial",
      "Ownership of process settings and trial data",
      "Disposition of trial product — sale, sampling or destruction",
      "Rework and second-attempt terms",
      "Confidentiality of the trial and its results",
      "Whether the trial commits either party to a commercial agreement",
      "Insurance and liability during the trial",
    ],
    clausesToWatch: [
      {
        clause: "Disposition of trial product",
        why: "Trial output may not meet specification, and without a clause it can be sampled, donated or sold in ways that put unfinished product in front of consumers.",
      },
      {
        clause: "Ownership of process settings",
        why: "Line settings developed during the trial are part of how the product is made, and a plant that owns them holds a piece of your process.",
      },
      {
        clause: "Cost of a failed trial",
        why: "Failed runs are common and expensive, and silence means every attempt is billed to the brand regardless of cause.",
      },
      {
        clause: "Definition of success",
        why: "Without agreed acceptance criteria, a trial that produces sellable but off-target product is treated as a success and invoiced accordingly.",
      },
      {
        clause: "Commitment to a commercial agreement",
        why: "A pilot clause that obliges you to place a minimum commercial volume turns a test into a contract you have not negotiated.",
      },
    ],
    cpgIssues: [
      "Minimum run sizes mean a pilot often produces far more product than a trial needs, and the excess has to be paid for and stored or destroyed.",
      "Changeover and sanitation time between products is billable plant time and is a real component of pilot cost, especially on allergen-controlled lines.",
      "Packaging minimums frequently exceed pilot volumes, so a trial can require buying a full pallet of cans or a full run of labels.",
      "Trial product usually carries no validated shelf life, so selling it commercially is a decision with quality consequences, not just a cost recovery.",
      "Yield loss at first run is typically much worse than at steady state, which distorts the cost per unit and should not be used as a planning number.",
      "Fill weights, seal integrity and carbonation are commonly out of specification on a first run and require adjustments that belong in the process record.",
      "Lot coding and traceability should be applied to trial product exactly as to commercial product, in case anything leaves the plant.",
    ],
    founderMistakes: [
      "Booking a pilot with no written cost allocation and receiving an invoice covering plant time, changeover, destroyed material and labour after a failed run.",
      "Selling trial product to recover cost before any shelf-life validation existed, then facing quality complaints in month three.",
      "Not securing ownership of the process settings developed at the trial, which made moving to a second plant a fresh development exercise.",
      "Buying a full minimum order of cans for a pilot that needed a fraction of them, and holding the rest as obsolete stock after a pack change.",
      "Agreeing a pilot that carried an automatic commitment to a minimum annual commercial volume.",
    ],
    negotiationPoints: [
      "Agree written cost allocation for each element of the trial, including a failed attempt and the reason it failed.",
      "Define success against the specification and agree who signs off.",
      "Require that trial product be quarantined and disposed of by agreement, not sold by default.",
      "Ask for ownership of process settings, trial data and the resulting process record.",
      "Negotiate a second attempt at reduced cost where the failure is attributable to the plant.",
      "Keep the pilot free of any binding commercial volume commitment.",
    ],
    redFlags: [
      "No cost allocation for a failed run.",
      "Plant ownership of process settings and trial data.",
      "Automatic commitment to commercial volumes triggered by the trial.",
      "Trial product released for sale with no specification sign-off.",
      "No definition of what a successful trial means.",
    ],
    beforeYouSign: [
      "A written trial plan with acceptance criteria and the sampling and testing that will decide it.",
      "A cost estimate covering plant time, changeover, materials and disposal.",
      "Agreement on what happens to the product that comes off the line.",
    ],
    related: [
      "pilot-run-agreement",
      "co-packer-agreement",
      "product-specification-sheet",
      "beverage-formulator-agreement",
      "quality-agreement",
      "sample-testing-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "sensory-testing-agreement",
    name: "Sensory Testing Agreement",
    category: "product-development",
    subcategory: "Testing and validation",
    aliases: ["Sensory Panel Agreement", "Taste Testing Agreement", "Consumer Sensory Research Agreement"],
    stages: ["development", "pre-launch", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Sensory Research Provider",
    whenYouNeedIt:
      "When running trained-panel or consumer sensory work: comparing formulation variants, benchmarking against a competitor, validating a reformulation, or testing whether a sugar reduction is detectable.",
    whatItDoes:
      "Commissions structured sensory evaluation, sets the protocol and panel design, and settles who owns the data and how the results may be used, including in marketing.",
    whyItMatters:
      "Sensory work becomes a marketing asset the moment it produces a favourable number, and that is exactly when its limits matter. A panel run to guide a reformulation decision is designed differently from a study intended to support a preference claim, and using the first as though it were the second is a problem you create for yourself. Settle the purpose, the protocol and the data ownership before the panel sits, because retrofitting a claim onto a study designed for something else does not work.",
    majorTerms: [
      "Study objective and whether results may support a public claim",
      "Panel type: trained descriptive panel or consumer panel",
      "Protocol, sample preparation and blinding",
      "Sample size and statistical approach",
      "Ownership of raw data and the report",
      "Right to use results in marketing and with retailers",
      "Participant recruitment, screening and consent",
      "Allergen disclosure to participants",
      "Confidentiality of unreleased product and of competitor benchmarks",
      "Retention and destruction of samples and personal data",
    ],
    clausesToWatch: [
      {
        clause: "Permitted use of results",
        why: "A provider restricting marketing use leaves you with an expensive study you cannot cite to a retailer or a consumer.",
      },
      {
        clause: "Ownership of raw data",
        why: "Owning a summary report but not the underlying data means you cannot re-analyse or defend the finding later.",
      },
      {
        clause: "Protocol and blinding",
        why: "An unblinded or poorly designed panel produces a number nobody outside the room will accept.",
      },
      {
        clause: "Participant consent and data handling",
        why: "Panels collect personal data and dietary information, which brings obligations that vary by jurisdiction.",
      },
      {
        clause: "Competitor sample handling",
        why: "Benchmarking against a competitor's product involves buying and presenting their goods, and how that is documented matters.",
      },
    ],
    cpgIssues: [
      "Panelists consume the product, so allergen disclosure and screening are a safety requirement rather than an administrative step.",
      "Sample preparation — serving temperature, vessel, portion size and time from opening — changes results materially and belongs in the protocol.",
      "Sensory drift across a shelf-life study is often the real question, which means the panel design has to be repeatable at intervals.",
      "Unreleased flavours and pack designs shown to consumer panels leak, so confidentiality and sample control matter for a pre-launch study.",
      "Trained panels give descriptive data useful for reformulation; consumer panels give preference data. Using one where you needed the other is a common and expensive mismatch.",
      "Reformulation driven by cost reduction is where sensory work earns its money, because the question is whether consumers can tell.",
    ],
    founderMistakes: [
      "Running a small informal taste test and citing the result as a preference claim in marketing.",
      "Commissioning a consumer panel when the development question needed a trained descriptive panel, and getting data that could not guide reformulation.",
      "Owning only the summary report, so a retailer's question about methodology could not be answered.",
      "Showing unreleased packaging to a consumer panel with no confidentiality or sample control, and seeing it posted publicly.",
    ],
    negotiationPoints: [
      "State the study purpose in the agreement, including whether results are intended to support a public claim.",
      "Ask for ownership of raw data as well as the report, with an unrestricted right to use results commercially.",
      "Agree the protocol in writing before recruitment, including blinding, preparation and statistical approach.",
      "Require documented allergen screening and participant consent.",
      "Require confidentiality and sample control for any unreleased product or packaging shown.",
    ],
    redFlags: [
      "Results restricted from marketing use where the purpose was a claim.",
      "Raw data retained by the provider.",
      "No documented allergen screening for consumed samples.",
      "Protocol left to be decided after recruitment.",
      "No confidentiality obligations on panelists for pre-launch product.",
    ],
    beforeYouSign: [
      "A written statement of the decision the study is meant to inform.",
      "The protocol, including panel type, sample size and blinding.",
      "Confirmation of how participant consent and allergen screening are handled.",
    ],
    related: [
      "product-testing-participant-release",
      "shelf-life-testing-agreement",
      "rd-agreement",
      "formula-development-agreement",
      "stability-testing-agreement",
      "product-development-agreement",
    ],
    attorneyReview: "depends",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "product-testing-participant-release",
    name: "Product Testing Participant Release",
    category: "product-development",
    subcategory: "Testing and validation",
    aliases: ["Taste Test Participant Release", "Consumer Trial Consent Form", "Panelist Release"],
    stages: ["development", "pre-launch", "launch"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Individual Participant",
    whenYouNeedIt:
      "Before anyone outside the company consumes an unreleased product: a home-use test, an in-office tasting, a consumer panel, a sampling of a prototype at an event.",
    whatItDoes:
      "Records that the participant was told what they are consuming, discloses allergens and ingredients, obtains their consent, and covers how their feedback and any images of them may be used.",
    whyItMatters:
      "Handing an unreleased food or beverage to a member of the public is a different act from handing them a finished product on shelf, because the product has not been through the checks that shelf products have. The release is the record that they were told what was in it and agreed to try it. It also settles the quieter question founders forget: whether the enthusiastic quote from a home-use tester can be printed on the pack or run as an ad.",
    majorTerms: [
      "Description of the product and its full ingredient list",
      "Allergen disclosure and acknowledgement",
      "Participant screening for allergies and dietary restrictions",
      "Consent to consume an unreleased product",
      "Ownership and permitted use of feedback and testimonials",
      "Consent to photography, video and quotation",
      "Adverse reaction reporting procedure",
      "Guardian consent for minors",
      "Confidentiality of the unreleased product and packaging",
      "Compensation or incentive provided",
    ],
    clausesToWatch: [
      {
        clause: "Allergen disclosure",
        why: "An incomplete disclosure defeats the purpose of the document and creates the exact risk it exists to manage.",
      },
      {
        clause: "Testimonial use rights",
        why: "Without an express grant, a favourable quote or an on-camera reaction cannot be used in marketing.",
      },
      {
        clause: "Adverse reaction reporting",
        why: "A defined contact and procedure means a reaction is captured and acted on rather than reaching you through social media.",
      },
      {
        clause: "Guardian consent",
        why: "Testing with children requires the guardian's consent and a narrower scope for any imagery.",
      },
      {
        clause: "Confidentiality of the prototype",
        why: "Home-use tests put unreleased packaging in consumers' kitchens, and without a confidentiality term it reaches social media before launch.",
      },
    ],
    cpgIssues: [
      "Prototype product frequently has no validated shelf life, so home-use tests need clear use-by instructions and a defined return or disposal step.",
      "Supplements and functional products carry active ingredients where screening questions matter more than they do for a snack.",
      "Ingredient lists at prototype stage change between rounds, so the disclosure has to match the specific batch handed to that participant.",
      "Sampling at events reaches people who have not been screened at all, which is a different risk profile from a recruited panel.",
      "Testimonials collected in testing are used in launch marketing constantly, and the disclosure obligations that attach to incentivised endorsements need to be handled.",
      "Prototype packaging often lacks a compliant label, which is another reason participants must be told exactly what they are receiving.",
    ],
    founderMistakes: [
      "Running a home-use test with an ingredient list that had changed since the form was written, so participants were told the wrong thing.",
      "Using a participant's enthusiastic quote in launch advertising with no permission to do so.",
      "Testing with children with no guardian consent on file.",
      "Handing prototype product to consumers with no use-by guidance and no way to reach them if a batch issue emerged.",
      "Failing to disclose that participants were compensated when their quotes were used publicly.",
    ],
    negotiationPoints: [
      "Attach the exact ingredient and allergen statement for the specific batch to each release.",
      "Include an express grant covering use of feedback, quotes, name and image in marketing, or state clearly that no such use is permitted.",
      "Add a named contact and a defined procedure for reporting any adverse reaction.",
      "Include confidentiality terms and a return or disposal instruction for prototype product.",
      "Where participants are compensated, record it, since it affects how any testimonial may be presented.",
    ],
    redFlags: [
      "No allergen disclosure or no screening step.",
      "A generic form reused across batches with different formulations.",
      "Testimonial use assumed rather than granted.",
      "No guardian consent process for minors.",
      "No adverse reaction reporting route.",
    ],
    beforeYouSign: [
      "The current ingredient and allergen statement for the exact batch being tested.",
      "A screening questionnaire covering allergies, dietary restrictions and relevant conditions.",
      "A decision on whether feedback will be used publicly, made before the test rather than after.",
    ],
    related: [
      "sensory-testing-agreement",
      "product-sampling-release",
      "model-release",
      "sample-testing-agreement",
      "event-sampling-agreement",
      "content-release",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Consent to consume an unreleased food or supplement, testing involving minors, and the use of incentivised testimonials all carry requirements that vary by jurisdiction.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "stability-testing-agreement",
    name: "Stability Testing Agreement",
    category: "product-development",
    subcategory: "Testing and validation",
    aliases: ["Accelerated Stability Study Agreement", "Product Stability Agreement", "Stability Study Contract"],
    stages: ["development", "pre-launch", "manufacturing"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Testing Laboratory or Development Partner",
    whenYouNeedIt:
      "Once a formula is close to final and before committing to a shelf-life date, packaging artwork or a retailer listing that depends on either.",
    whatItDoes:
      "Commissions a structured study of how the product changes over time under defined conditions, covering the protocol, the pull points, the tests at each point, and who interprets the results.",
    whyItMatters:
      "Stability data is what turns a formula into a product you can put a date on. It is also a long-lead item that founders start too late: a real-time study takes as long as the shelf life you are trying to prove, and accelerated conditions are a prediction rather than the answer. The commercial consequence of starting late is a launch date built on an assumed shelf life, and the consequence of getting it wrong is product separating, browning or losing potency in a distributor's warehouse.",
    majorTerms: [
      "Storage conditions and whether accelerated, real-time or both",
      "Pull points and test schedule",
      "Analytical tests performed at each pull",
      "Acceptance criteria for each attribute",
      "Packaging used in the study and whether it matches production",
      "Who interprets the results and signs the conclusion",
      "Ownership of data and the right to share it",
      "Sample storage, chain of custody and retention",
      "Handling of a failed pull point",
      "Cost and timeline, including re-runs",
    ],
    clausesToWatch: [
      {
        clause: "Packaging used in the study",
        why: "Stability is a property of the product in its package, so a study run in a different container does not carry over to the one you sell.",
      },
      {
        clause: "Who signs the shelf-life conclusion",
        why: "Labs commonly provide data without a conclusion, leaving the brand to make a decision it may not be equipped to make.",
      },
      {
        clause: "Acceptance criteria",
        why: "Without agreed criteria per attribute, a study produces numbers with no verdict, and the argument happens later at a retailer's expense.",
      },
      {
        clause: "Failed pull point procedure",
        why: "A failure mid-study raises immediate questions about product already produced, and the response should be planned rather than improvised.",
      },
      {
        clause: "Data ownership and sharing",
        why: "Retailers, insurers and buyers all ask for stability data, and a restriction on sharing it makes the study far less useful.",
      },
    ],
    cpgIssues: [
      "Accelerated studies predict; real-time studies confirm. Launching on accelerated data alone is a common, deliberate commercial choice that should be made knowingly.",
      "The study must run in production packaging, because oxygen barrier, light transmission and headspace all drive the result.",
      "Attributes worth tracking vary by product: separation and sedimentation in a beverage, oxidation and rancidity in a bar, potency loss in a supplement, colour and texture in a confection.",
      "Overage decisions for functional actives depend on how potency declines across shelf life, and that decision is a label question as well as a formulation one.",
      "Retailers and distributors often specify minimum remaining shelf life on receipt, so the study result determines how much of the life is actually usable in trade.",
      "Cold chain and ambient distribution give completely different exposure profiles, so the study conditions should reflect how the product will actually travel.",
      "Temperature abuse in transit and at retail is real, and some studies include an abuse leg for that reason.",
    ],
    founderMistakes: [
      "Starting stability work after packaging was ordered, then discovering the product needed a different barrier.",
      "Running the study in glass jars for convenience and launching in a pouch with completely different results.",
      "Setting a shelf-life date from accelerated data alone with no real-time confirmation running behind it.",
      "Ignoring the retailer's minimum-remaining-life requirement, which made a nine-month shelf life effectively six in trade.",
      "Receiving a data set with no conclusion and no acceptance criteria, and picking a date without a basis for it.",
    ],
    negotiationPoints: [
      "Require the study to run in final production packaging, and start real-time alongside accelerated from day one.",
      "Agree acceptance criteria per attribute before the first pull.",
      "Ask for a written interpretation and a recommended shelf life, or arrange for a qualified party to provide one.",
      "Secure ownership of the data with an unrestricted right to share it with retailers, insurers and buyers.",
      "Agree in advance what happens, commercially and operationally, if a pull point fails.",
    ],
    redFlags: [
      "A study run in packaging that differs from production.",
      "No acceptance criteria agreed in advance.",
      "No written conclusion or recommendation from anyone.",
      "Restrictions on sharing the data with your customers.",
      "No real-time study running behind an accelerated one.",
    ],
    beforeYouSign: [
      "Final production packaging specification and confirmed supply of study units.",
      "A written list of attributes to track and the acceptance criteria for each.",
      "Your largest retailer's minimum remaining shelf-life requirement.",
    ],
    related: [
      "shelf-life-testing-agreement",
      "sample-testing-agreement",
      "beverage-formulator-agreement",
      "quality-agreement",
      "packaging-specification-agreement",
      "product-specification-sheet",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "shelf-life-testing-agreement",
    name: "Shelf-Life Testing Agreement",
    category: "product-development",
    subcategory: "Testing and validation",
    aliases: ["Shelf Life Validation Agreement", "Best-By Date Substantiation Agreement", "Shelf-Life Study Contract"],
    stages: ["development", "pre-launch", "manufacturing"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Laboratory or Food Safety Consultant",
    whenYouNeedIt:
      "Before printing a date on a package or committing to a retailer's shelf-life requirement — and again after any formulation, process or packaging change.",
    whatItDoes:
      "Establishes and substantiates the date that goes on the product: the study design, the microbiological and quality endpoints, the conditions tested, and who is accountable for the conclusion.",
    whyItMatters:
      "The date on the pack is a claim you are making to every buyer and every consumer, and it drives real commercial mechanics: a distributor's acceptance window, a retailer's minimum remaining life, and whether product returns as unsaleable. Founders often take a shelf life from a co-packer's estimate or a competitor's pack and print it. If the product does not hold, the cost is not a technical problem — it is returns, deductions and a retailer relationship that does not survive a second incident.",
    majorTerms: [
      "Study design and whether a challenge study is included",
      "Microbiological and quality endpoints",
      "Storage conditions and any abuse testing",
      "Packaging and format tested",
      "Who determines and signs off the final shelf life",
      "Date format and coding responsibility",
      "Re-validation triggers after formulation or process change",
      "Ownership of the study and the right to share it",
      "Timeline and cost, including re-runs",
      "Documentation retained for retailer and insurer requests",
    ],
    clausesToWatch: [
      {
        clause: "Responsibility for the final date",
        why: "A study provides evidence; someone has to decide the date, and if nobody is named the brand ends up deciding without support.",
      },
      {
        clause: "Re-validation triggers",
        why: "A supplier substitution or a process change can invalidate the study, and without triggers nobody revisits it.",
      },
      {
        clause: "Challenge study inclusion",
        why: "For some product types this is what a co-packer or a retailer will actually ask for, and it is a distinct and more expensive piece of work.",
      },
      {
        clause: "Abuse conditions",
        why: "Product does not travel under ideal conditions, and a study run only at ideal storage overstates real-world life.",
      },
      {
        clause: "Documentation and record retention",
        why: "Retailers, insurers and buyers request substantiation years later, and a study you cannot produce is one you effectively did not run.",
      },
    ],
    cpgIssues: [
      "Retailers commonly require a minimum proportion of remaining shelf life on receipt, so the total life determines how long you actually have to sell through.",
      "Date coding and lot coding are the mechanism behind traceability and any recall, and they are set at the plant rather than in the study.",
      "Distributors will reject or discount product with short remaining life, which turns a shelf-life shortfall directly into deductions.",
      "Format changes — single-serve to multipack, glass to can, ambient to chilled — usually require re-validation rather than an adjustment.",
      "Ingredient substitutions driven by supply problems can change water activity, pH or preservative efficacy, which is a re-validation trigger even when the product tastes identical.",
      "Products relying on a preservation system rather than refrigeration typically require a more formal validation, which a co-packer will ask to see before running.",
      "Unsaleable returns and spoils allowances are negotiated on the assumption the printed date is right.",
    ],
    founderMistakes: [
      "Printing a twelve-month date taken from a competitor's pack with no study behind it, and taking returns from month eight onwards.",
      "Substituting an ingredient during a supply shortage without re-validating, and losing the shelf life the label promised.",
      "Running the study at ideal storage only, and finding real distribution conditions produced a different outcome.",
      "Failing to keep the substantiation documentation, then being unable to answer a retailer's request years later.",
      "Ignoring a retailer's minimum remaining life requirement when planning production timing, which stranded inventory that was still well within date.",
    ],
    negotiationPoints: [
      "Name who determines the final shelf life and require a written, signed conclusion.",
      "Include abuse or elevated-temperature conditions reflecting real distribution.",
      "Agree re-validation triggers covering formulation, supplier, process and packaging changes.",
      "Secure ownership of the study with the right to provide it to retailers, distributors and insurers.",
      "Confirm whether a challenge study is required by your co-packer or your accounts before scoping the work.",
    ],
    redFlags: [
      "A shelf life set by estimate rather than by study.",
      "No named party responsible for the conclusion.",
      "No re-validation triggers.",
      "Study conditions that do not reflect how the product is distributed.",
      "No retained documentation package.",
    ],
    beforeYouSign: [
      "Your retailers' and distributors' minimum remaining shelf-life requirements in writing.",
      "Confirmation from your co-packer of what validation they require before running the product.",
      "The final packaging specification, since the study is only valid for what was tested.",
    ],
    related: [
      "stability-testing-agreement",
      "quality-agreement",
      "food-safety-agreement",
      "product-specification-sheet",
      "sample-testing-agreement",
      "co-packer-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
];

export default PRODUCT_DEVELOPMENT_DOCS;
